import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc';
import {
  Button,
  Form,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { Field, useFormikContext } from 'formik';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faBars } from '@fortawesome/pro-solid-svg-icons/faBars';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';

import { faHandPointRight } from '@fortawesome/pro-regular-svg-icons/faHandPointRight';

import Placeholder from 'components/Placeholder';
import FA from 'utils/FA';
import {
  fields as fieldsDuck,
  reviewFields as reviewFieldsDuck
} from '../../../ducks';
import { ensureKeyed } from '../../../utils';
import { duckify, filterDuck } from '../../../utils/duckify';
import ReviewPage from './ReviewPage';
import './styles/ColumnsAndFilters.scss';

const SortableListItem = SortableElement(({
  item,
  sortIndex,
  isSubmitting,
  action
}) => (
  <li className='review-sortable-list-item'>
    <div className='review-sortable-list-reorder'>
      <FA icon={faBars} nbsp />
    </div>
    <div className='review-sortable-list-column-field-name'>
      {item.field_name}
    </div>
    <div className='review-sortable-list-column-option'>
      <Field
        as={Form.Switch}
        name={`reviewFields.${sortIndex}.filter_option`}
        checked={item.filter_option}
        disabled={isSubmitting}
      />
    </div>
    <div className='review-sortable-list-column-actions'>
      <Button variant='danger' size='xs' onClick={() => action(sortIndex)}>
        <FA icon={faTimes} nbsp />
      </Button>
    </div>
  </li>
));

const SortableList = SortableContainer(({
  items,
  action,
  isSubmitting
}) => (
  <ul className='review-sortable-list'>
    {items.map((item, index) => (
      <SortableListItem
        key={item.id}
        index={index}
        sortIndex={index}
        item={item}
        isSubmitting={isSubmitting}
        disabled={isSubmitting}
        action={action}
      />
    )).map(ensureKeyed('columns_and_filters_list_item'))}
  </ul>
));

const ColumnsAndFiltersList = ({
  fields,
  setItemPendingDeleteIndex,
  setShowConfirmDelete
}) => {
  const [showAddField, setShowAddField] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const { values, isSubmitting, setFieldValue } = useFormikContext();

  return (
    <div className='review-sub-section-columns review-sub-section'>
      <div className='review-note'>
        <div className='review-note-header'>
          <FA icon={faHandPointRight} nbsp />
          Note:
        </div>
        Review lists include both:
        <ul>
          <li>Each reader’s list of reviews (”My Assigned Reviews” and “My Completed Reviews”)</li>
          <li>The review administrator’s “Review Summary” list page</li>
          <li>The Reader Report page (filters only)</li>
        </ul>
        Columns for each rating category and the combined average
        of those ratings will be displayed automatically, and cannot be reordered.
      </div>
      <div className='review-include-message'>
        Choose columns, set them as filter options, or drag and drop to reorder:
      </div>
      <div className='review-column-list review-sortable-list-container'>
        <div className='review-sortable-list-header-row'>
          <div className='review-sortable-list-column-field-name review-sortable-list-header'>
            Field Name
          </div>
          <div className='review-sortable-list-column-option'>
            Include as a filter option?&nbsp;
            <OverlayTrigger
              overlay={<Tooltip>Allows you to filter the list by selecting the field values you want to see</Tooltip>}
            >
              <FA icon={faInfoCircle} nbsp />
            </OverlayTrigger>
          </div>
          <div className='review-sortable-list-column-actions'>
            Actions
          </div>
        </div>
        <SortableList
          lockAxis='y'
          distance={10}
          lockToContainerEdges
          items={values.reviewFields.filter(field => field.section === 'review_list')}
          action={index => {
            setItemPendingDeleteIndex(index);
            setShowConfirmDelete(true);
          }}
          isSubmitting={isSubmitting}
          onSortEnd={({ newIndex, oldIndex }) => {
            if (newIndex !== oldIndex) {
              setFieldValue('reviewFields', arrayMove(values.reviewFields, oldIndex, newIndex));
            }
          }}
        />
        <Button
          variant='link'
          className='review-settings-add-field'
          onClick={() => setShowAddField(!showAddField)}
          disabled={isSubmitting}
        >
          <FA icon={faPlusCircle} nbsp />
          Add Another Field
        </Button>
        {showAddField && (
          <Form.Group className='review-settings-add-field-container'>
            <div className='review-settings-add-field-selector'>
              <Field
                as={Select}
                options={fields}
                labelKey='display_name'
                valueKey='id'
                value={selectedValue}
                onChange={selected => setSelectedValue(selected)}
              />
            </div>
            <div className='review-settings-add-field-buttons'>
              <Button
                onClick={() => setFieldValue(
                  'reviewFields',
                  [
                    ...values.reviewFields,
                    {
                      section: 'review_list',
                      field_id: selectedValue.id,
                      field_name: selectedValue.display_name,
                      filter_option: false
                    }
                  ]
                )}
                disabled={(!selectedValue && selectedValue !== '') || isSubmitting}
              >
                <FA icon={faCheck} nbsp />
              </Button>
              <Button variant='light' onClick={() => setShowAddField(false)}>
                <FA icon={faTimes} nbsp />
              </Button>
            </div>
          </Form.Group>
        )}
      </div>
    </div>
  );
};

ColumnsAndFiltersList.propTypes = {
  /** Gender options enabled boolean from current customer */
  fields: PropTypes.arrayOf(PropTypes.shape({})),

  /** Callback to set the item pending delete hwen delete is selected */
  setItemPendingDeleteIndex: PropTypes.func,

  /** Callback to display the confirm delete modal */
  setShowConfirmDelete: PropTypes.func
};

ColumnsAndFiltersList.defaultProps = {
  fields: [],
  setItemPendingDeleteIndex: () => false,
  setShowConfirmDelete: () => false
};

const ColumnsAndFilters = ({
  className,
  fields,
  reviewFields,
  route,
  allDucksReady,
  schoolYearId,
  saveReviewFields,
  deleteReviewField,
  reloadDucks
}) => {
  const [formikValues, setFormikValues] = useState({
    reviewFields: reviewFields.filter(field => field.section === 'review_list')
  });
  const [itemPendingDeleteIndex, setItemPendingDeleteIndex] = useState();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  /** Initialize Formik state and update to ducks when year is changed */
  useEffect(() => {
    if (allDucksReady) {
      setFormikValues({ reviewFields: reviewFields.filter(field => field.section === 'review_list') });
    }
  }, [allDucksReady, schoolYearId]);

  const onSubmit = ({ values }) => saveReviewFields(values.reviewFields, schoolYearId);

  const handleDelete = (formikContext) => {
    const { values, setFieldValue } = formikContext;
    const { id } = values.reviewFields[itemPendingDeleteIndex];
    const array = [...values.reviewFields];

    array.splice(itemPendingDeleteIndex, 1);
    setShowConfirmDelete(false);

    /** If item needs to be deleted from server db send delete */
    if (id) {
      return (
        deleteReviewField({ id })
          .then(() => setFieldValue('reviewFields', array))
          .then(() => setFormikValues({
            reviewFields: array
          }))
      );
    }

    setFieldValue('reviewFields', array);

    return Promise.resolve({ skipFlash: true });
  };

  return (
    <ReviewPage
      className={className}
      header='Review List Columns & Filters'
      headerHelperText='Designate fields to display as columns on review list pages.'
      initialFormikValues={formikValues}
      route={route}
      onSubmit={onSubmit}
      showConfirmDelete={showConfirmDelete}
      onClose={() => setShowConfirmDelete(false)}
      onConfirm={handleDelete}
      reloadDucks={reloadDucks}
    >
      <Placeholder.loading
        ready={allDucksReady}
        content={() => (
          <ColumnsAndFiltersList
            fields={fields}
            setItemPendingDeleteIndex={setItemPendingDeleteIndex}
            setShowConfirmDelete={setShowConfirmDelete}
          />
        )}
      />
    </ReviewPage>
  );
};

ColumnsAndFilters.propTypes = {
  /** Classname prop */
  className: PropTypes.string,

  /** Current Track Silo Selected School Id */
  schoolYearId: PropTypes.string.isRequired,

  /** duck that provides all available fields */
  fields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    display_name: PropTypes.string,
    name: PropTypes.string
  })).isRequired,

  /** duck that provides fields selected for review */
  reviewFields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    field_id: PropTypes.string,
    field_name: PropTypes.string,
    section: PropTypes.string,
    filter_option: PropTypes.bool
  })).isRequired,

  /** React Router Route object */
  route: PropTypes.shape({}).isRequired,

  /** Allducksready boolean */
  allDucksReady: PropTypes.bool.isRequired,

  /** Submit Ducks Function */
  saveReviewFields: PropTypes.func.isRequired,

  /** Delete Ducks Function */
  deleteReviewField: PropTypes.func.isRequired,

  /** marks ducks dirty for reload */
  reloadDucks: PropTypes.func.isRequired
};

ColumnsAndFilters.defaultProps = {
  className: ''
};

const mapDucksToProps = props => {
  const { schoolYearId } = props;
  return {
    reviewFields: filterDuck(reviewFieldsDuck, { school_year_id: schoolYearId }),
    fields: filterDuck(fieldsDuck, { selectable: true })
  };
};

const mapDispatchToProps = dispatch => ({
  saveReviewFields: (reviewFields, schoolYearId) => (
    dispatch(reviewFieldsDuck.actions.bulkUpdate(
      reviewFields,
      { school_year_id: schoolYearId }
    )))
    .then(reviewFieldsDuck.actions.denormalizeObjects),
  deleteReviewField: field => (
    dispatch(reviewFieldsDuck.actions.remove(field))
      .then(reviewFieldsDuck.actions.denormalizeObjects)
  ),
  reloadDucks: () => dispatch(reviewFieldsDuck.actions.markDirty())
});

export default duckify(mapDucksToProps, () => {}, mapDispatchToProps)(ColumnsAndFilters);
